import {metadaApi} from '@dms/api/base';

import {
  GetFacebookCredentialsApiArg,
  GetFacebookCredentialsApiResponse,
  FacebookCredentialsResponseSchema,
  PatchFacebookCredentialsApiArg,
  PatchFacebookCredentialsApiResponse,
  PatchFacebookCredentialsApiArgSchema,
  GetMetaAuthorizationApiArg,
  GetMetaAuthorizationApiResponse,
  MetaAuthorizationResponseSchema,
  GenerateFacebookLoginUrlApiArg,
  GenerateFacebookLoginUrlApiResponse,
  FacebookLoginUrlResponseSchema,
  PostFacebookCallbackApiArg,
  PostFacebookCallbackApiResponse,
  PostFacebookCallbackApiArgSchema,
  FacebookCallbackResponseSchema,
  DeleteFacebookAuthorizationApiArg,
  DeleteFacebookAuthorizationApiResponse,
  PatchFacebookAuthorizationApiArg,
  PatchFacebookAuthorizationApiResponse,
  PatchFacebookAuthorizationApiArgSchema,
  PatchFacebookAuthorizationResponseSchema,
} from './types';

export const metadaFacebookCredentialsApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getFacebookCredentials: build.query<
      GetFacebookCredentialsApiResponse,
      GetFacebookCredentialsApiArg & {'X-Tenant': string}
    >({
      query: (queryArg) => ({
        url: '/app/v1/facebook-credentials',
        headers: {'X-Tenant': queryArg['X-Tenant']},
      }),
      providesTags: ['facebookCredentials'],
      extraOptions: {
        responseSchema: FacebookCredentialsResponseSchema,
      },
    }),
    patchFacebookCredentials: build.mutation<
      PatchFacebookCredentialsApiResponse,
      PatchFacebookCredentialsApiArg & {'X-Tenant': string}
    >({
      query: (queryArg) => ({
        url: '/app/v1/facebook-credentials',
        method: 'PATCH',
        body: queryArg.facebookCredentialsRequestBody,
        headers: {'X-Tenant': queryArg['X-Tenant']},
      }),
      invalidatesTags: ['facebookCredentials'],
      extraOptions: {
        requestSchema: PatchFacebookCredentialsApiArgSchema,
      },
    }),
    getMetaAuthorization: build.query<
      GetMetaAuthorizationApiResponse,
      GetMetaAuthorizationApiArg & {'X-Tenant': string}
    >({
      query: (queryArg) => ({
        url: '/app/v1/meta-authorization',
        headers: {'X-Tenant': queryArg['X-Tenant']},
      }),
      providesTags: ['metaAuthorization'],
      extraOptions: {
        responseSchema: MetaAuthorizationResponseSchema,
      },
    }),
    generateFacebookLoginUrl: build.query<
      GenerateFacebookLoginUrlApiResponse,
      GenerateFacebookLoginUrlApiArg & {'X-Tenant': string}
    >({
      query: (queryArg) => ({
        url: '/app/v1/facebook-login-url',
        headers: {'X-Tenant': queryArg['X-Tenant']},
      }),
      providesTags: ['facebookLoginUrl'],
      extraOptions: {
        responseSchema: FacebookLoginUrlResponseSchema,
      },
    }),
    postFacebookCallback: build.mutation<
      PostFacebookCallbackApiResponse,
      PostFacebookCallbackApiArg & {'X-Tenant': string}
    >({
      query: (queryArg) => ({
        url: '/app/v1/auth/facebook/callback',
        method: 'POST',
        headers: {'X-Tenant': queryArg['X-Tenant']},
        params: {
          code: queryArg.code,
          state: queryArg.state,
          error: queryArg.error,
        },
      }),
      invalidatesTags: ['metaAuthorization'],
      extraOptions: {
        requestSchema: PostFacebookCallbackApiArgSchema,
        responseSchema: FacebookCallbackResponseSchema,
      },
    }),
    deleteFacebookAuthorization: build.mutation<
      DeleteFacebookAuthorizationApiResponse,
      DeleteFacebookAuthorizationApiArg & {'X-Tenant': string}
    >({
      query: (queryArg) => ({
        url: '/app/v1/auth/facebook/delete',
        method: 'DELETE',
        headers: {'X-Tenant': queryArg['X-Tenant']},
      }),
      invalidatesTags: ['metaAuthorization'],
    }),
    patchFacebookAuthorization: build.mutation<
      PatchFacebookAuthorizationApiResponse,
      PatchFacebookAuthorizationApiArg & {'X-Tenant': string}
    >({
      query: (queryArg) => ({
        url: '/app/v1/auth/facebook/enable',
        method: 'PATCH',
        body: queryArg.patchFacebookAuthorizationRequestBody,
        headers: {'X-Tenant': queryArg['X-Tenant']},
      }),
      invalidatesTags: ['metaAuthorization'],
      extraOptions: {
        requestSchema: PatchFacebookAuthorizationApiArgSchema,
        responseSchema: PatchFacebookAuthorizationResponseSchema,
      },
    }),
  }),
});

export const {
  useGetFacebookCredentialsQuery,
  usePatchFacebookCredentialsMutation,
  useGetMetaAuthorizationQuery,
  useGenerateFacebookLoginUrlQuery,
  useLazyGenerateFacebookLoginUrlQuery,
  usePostFacebookCallbackMutation,
  useDeleteFacebookAuthorizationMutation,
  usePatchFacebookAuthorizationMutation,
} = metadaFacebookCredentialsApi;
